// freightmancerlvl99.component.ts
import { booleanAttribute, Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import {
  trigger,
  transition,
  style,
  animate,
  query,
  group,
} from '@angular/animations';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  FormArray,
  Validators,
  FormControl,
} from '@angular/forms';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, Subscription } from 'rxjs';

import type { LadingData } from '../rates/freightmancers-rate-friend/freightmancers-rate-friend.component';


import { MenuService } from '../menu.service';
import {
  ContactAutocompleteService,
  ContactAutocompleteResult,
  ContactDetails,
} from '../services/contacts/contact-autocomplete.service';

// Adjust the import path below if your models file is located elsewhere.
import {
  IFreightmancerLvl99,
  IWizardStep,
  IDefaultService,
  ILadingScope,
  IRaterArgument,
  IRaterPallet,
  IFreightmancerPayload,
  IPalletProductPayload,
  IAddressOption,
  TServiceTypeMap,
} from './freightmancerlvl99.models';
import { controllers } from 'chart.js';
import { NumberCellEditor } from 'ag-grid-community';
import { DISPATCH_FREIGHT_PHASE1_ENABLED } from '../dispatch-freight/dispatch-freight.feature';
import {
  createEmptyDispatchFreightDraft,
} from '../dispatch-freight/domain/dispatch-freight.builder';
import {
  DispatchFreightDraft,
  DispatchAppendNewLegDetails,
  DispatchRateQuoteResponse,
  DispatchRateUiState,
  DispatchRatingRequest,
} from '../dispatch-freight/models/dispatch-freight.models';
import {
  adaptFreightmancerDispatch,
  FreightmancerDispatchBridge,
} from '../dispatch-freight/adapters/freightmancer-dispatch.adapter';
import { DispatchFreightApiService } from '../dispatch-freight/data/dispatch-freight-api.service';
import {
  dispatchFreightToLegacyPalletProducts,
  DispatchFreightLegacyPayloadError,
} from './adapters/dispatch-freight-lading-payload.adapter';

const LADING_SCOPES: ILadingScope[] = [
  { id: 1 , name: 'Tradeshow',          description: 'Freight destined for a tradeshow or convention center' },
  { id: 2 , name: 'Storage',            description: 'Freight stored temporarily; warehousing fees may apply' },
  { id: 3 , name: 'USA Crossborder',    description: 'Freight crosses from the US into Canada (requires PARS)' },
  { id: 4 , name: 'CANADA Crossborder', description: 'Freight crosses from Canada into the US (requires PAPS)' },
  { id: 5 , name: 'Air Freight',        description: 'Freight transported via air (commercial or charter)' },
  { id: 6 , name: 'Ocean Freight',      description: 'Freight moved via ocean container shipping' },
  { id: 7 , name: 'Local Delivery',     description: 'Short-haul freight delivered within a local region' },
  { id: 8 , name: 'Dock to Dock',       description: 'Freight moves from one dock facility to another' },
  { id: 9 , name: 'White Glove',        description: 'Premium inside delivery with high-touch handling' },
  { id: 10, name: 'Military Site',      description: 'Pickup or delivery at a military base or federal site' },
  { id: 11, name: 'Client Generated',   description: 'Order created via client portal/API/EDI' },
  { id: 12, name: 'Consolidation',      description: 'Freight grouped at a hub or consolidation point' },
  { id: 13, name: 'Trailer Build',      description: 'Freight part of a trailer construction or retrofit' },
  { id: 14, name: 'Household',          description: 'Household goods, may require customs/residential handling' },
  { id: 15, name: 'Plug and Play',      description: 'Tech-ready freight (e.g., gym/medical gear with install prep)' },
  { id: 16, name: 'Extraction',         description: 'Freight picked up from site (e.g., uninstall/removal)' },
  { id: 17, name: 'Disposal',           description: 'Freight removed for recycling or discard' },
  { id: 18, name: 'Residential',        description: 'Pickup or delivery at a residential address' },
  { id: 19, name: 'Commercial',         description: 'Pickup or delivery at a commercial address' },
  { id: 20, name: 'Return Merchandise', description: 'RMA or customer-initiated return' },
  { id: 21, name: 'Inventory Intake',   description: 'Freight received into inventory at warehouse' },
  { id: 22, name: 'Sort Scan',          description: 'Freight sorted and scanned into system' },
  { id: 23, name: 'Sort Only',          description: 'Freight sorted but not scanned' },
  { id: 24, name: 'Term Storage',       description: 'Freight held long-term at terminal or staging area' },
  { id: 25, name: 'Intermodal',         description: 'Freight uses multiple transport modes (e.g., truck + rail)' },
  { id: 26, name: 'Job Site',           description: 'Delivery to job site requiring special handling/equipment' },
  { id: 27, name: 'Yarded Facility',    description: 'Freight staged at a fenced or permanent yard' },
];

const SCOPE_REQUIRED_FIELDS: Record<number, string[]> = {
  3: ['custom_broker_name'],                                              // USA Crossborder
  4: ['custom_broker_name'],                                              // CANADA Crossborder
  5: ['additional_info.shipment_number'],                                 // Air Freight
  6: ['additional_info.booking_number', 'additional_info.release_number'], // Ocean Freight
};

const LADING_STATUSES: string[] = [
  'In Transit',
  'Loaded Shipper',
  'Arrived Shipper',
  'Booked',
  'Unassigned',
  'Qualify',
  'En Route Shipper'
];

@Component({
  selector: 'app-freightmancerlvl99',
  templateUrl: './freightmancerlvl99.component.html',
  styleUrls: ['./freightmancerlvl99.component.scss'],
  animations: [
    trigger('stepAnim', [
      transition(':increment', group([
        query(':enter', [
          style({ transform: 'translateX(100%)' }),
          animate('300ms ease-out', style({ transform: 'translateX(0%)' }))
        ], { optional: true }),
        query(':leave', [
          animate('300ms ease-out', style({ transform: 'translateX(-100%)' }))
        ], { optional: true }),
      ])),
      transition(':decrement', group([
        query(':enter', [
          style({ transform: 'translateX(-100%)' }),
          animate('300ms ease-out', style({ transform: 'translateX(0%)' }))
        ], { optional: true }),
        query(':leave', [
          animate('300ms ease-out', style({ transform: 'translateX(100%)' }))
        ], { optional: true }),
      ])),
    ])
  ],
})
export class Freightmancerlvl99Component implements OnInit, OnDestroy, IFreightmancerLvl99 {
  @ViewChild('customerProfileSaveDialog') customerProfileSaveDialog!: TemplateRef<any>;

  /* ────────────────────────────────────────────────────────────
     Wizard nav
  ──────────────────────────────────────────────────────────── */
  steps: IWizardStep[] = [
    { label: 'Customer',      icon: 'fa-user' },
    { label: 'Shipper',       icon: 'fa-box' },
    { label: 'Consignee',     icon: 'fa-box-open' },
    { label: 'Manifest Load', icon: 'fa-truck-loading' },
    { label: 'Summon Rates',  icon: 'fa-dollar-sign' },
    { label: 'Bestow Status', icon: 'fa-pen-to-square'}
  ];
  active = 0;
  activeStep: BehaviorSubject<number> = new BehaviorSubject<number>(0);

  /* ────────────────────────────────────────────────────────────
     User & Rater
  ──────────────────────────────────────────────────────────── */
  userEmail = '';
  showRater = false;
  raterArg: any;  
  carrierContactUuid = '';

  /**
   * Phase-1 seam only. The compile-time flag defaults false, preserving the
   * existing pallet editor and rate friend until activation QA is complete.
   */
  readonly dispatchFreightPhase1Enabled = DISPATCH_FREIGHT_PHASE1_ENABLED;
  dispatchFreightDraft: DispatchFreightDraft = createEmptyDispatchFreightDraft();
  dispatchFreightRatingRequest: DispatchRatingRequest | null = null;
  dispatchRateState: DispatchRateUiState = 'idle';
  dispatchRateResponse: DispatchRateQuoteResponse | null = null;
  dispatchRateError = '';
  private dispatchRateSubscription: Subscription | null = null;
  private readonly dispatchContextSubscriptions = new Subscription();
  private dispatchRateRequestSequence = 0;

  /* ────────────────────────────────────────────────────────────
     Reactive forms
  ──────────────────────────────────────────────────────────── */
  customerForm!: FormGroup;
  shipperForm!: FormGroup;
  consigneeForm!: FormGroup;
  loadDetailsForm!: FormGroup;
  ratesForm!: FormGroup;
  statusForm!: FormControl;
 
  /* ────────────────────────────────────────────────────────────
     Lading scopes & select data
  ──────────────────────────────────────────────────────────── */
  ladingScopes: ILadingScope[] = LADING_SCOPES;
  ladingScopesById: Record<number, ILadingScope> = {};
  ladingStatuses: string[] = LADING_STATUSES;

  serviceCategories: string[] = [
    'Accessorial Service', 'Administration Service', 'Build Service', 'Customs Service',
    'Delivery Service', 'Equipment Fee', 'Labourer Service', 'Pickup Service',
    'Service Program', 'Surcharge Service', 'Tradeshow Service', 'Trailer Build Service'
  ];

  serviceTypes: TServiceTypeMap = {
    'Accessorial Service': ['Heated Service','Segregation Charge','Reworking Freight','Survey','Storage in Transit','Scale Ticket','Truck Furnished Not Used','Inspection Fee','Weight Inspection Fee','Stop Off','Refused','Expedite','Tolls','Over The Road','Tuning','Trailer Drop','Courier','Unloading','Sortation Charge','Redirect','Bulky Article','Trailer Repair','Detention','Forklift','Supplies','Marking Tagging','Drayage','Linked Expense','Service Charge','Layover','Skid Wrapping','Maintenance','Cartage','White Glove Service','Dangerous Goods','Haz Mat','Skid Fee','Tarping','Disposal Fee','Extraction','Crossdock','Off Line Miles','Scrap Yard Fees','Waiting Time','Return to Shipper','DDP Charges','Barge Service','Linehaul','Export Shipping Charge','Minimum Charge','Stair Crawler','Trailer Shunt','Pallet Allowance','Site Inspection','Loading','Truck Overflow','Exclusive Use','Facility Fee','Linehaul Inbound','Change in Terms of Pay','Linehaul Outbound','Team Service','Inward Charge','Single Shipment','Waiting Overtime','Beyond Charge','Container Lift Crane','Tilt Load Lift Equipment','Correct BOL Charge','Trailer Storage','Materials'],
    'Administration Service': ['Cancellation','Service Charge','Traffic Violation Fee','Administrative Fee','FedEx Order Processing','Parking Infraction','Insurance Cover','Documentation Charge','Management Fee','Additional PO Amount','Processing Fee','Credit Card Processing'],
    'Build Service': ['Repair','Pallets (Used)','Storage','Stretch Wrap','Build Consulting','License Plates','Delivery','Debris Removal','Build Materials','Labeling','Truck Wash','Shelf Cover Storage','Trailer Construction','Maintenance','Shelf Covers','Cleaning Service','Permit Fee Service','Missed Pickup','Shunt','GPS Tracking','Detention','Wrap Branding','Cargo Nets','Waiting Time','Trailer Purchase','Carton Strapping'],
    'Customs Service': ['Customs Devanning','Bond with Re-Delivery','Bond Charge','Border Delay','Border Security','Duty','Border Crossing','Customs Exam','Customs Brokerage','Customs Detention'],
    'Delivery Service': ['Delivery Residential','Delivery Advance Hours','Delivery After Hours','Delivery Appointment','Split Delivery','Delivery Time Specific','Delivery Notification','Delivery Certified','Delivery Saturday','Delivery Date Time Specific','Delivery Extra','Delivery Straight Truck','Delivery Rate','Delivery Attempted','Delivery Detention','Delivery Inside','Delivery Downtown','Delivery Back Door','Delivery Tailgate','2nd Truck','Delivery Highcost Area','Re-Delivery','Delivery Weekend','Delivery Sunday'],
    'Equipment Fee': ['Oversized Box Outbound','Oversized Box Inbound','Box / Carton Outbound','Reefer Service','Air Freight Service','Inbound Container Received','Box / Carton Inbound','Equipment Rental','Intermodal','Inbound Container','Trailer Rental Charge','Ocean Container','Pallet-In Inbound','Pallet-In Outbound'],
    'Labourer Service': ['Lumper Handling','Handling Surcharge','Helper Overtime','Warehouse Labour','Installation','Handling Class CAB','Labour','Handling Base Charge','Union Labour Overtime','Handling Extra Crew Charge','Driver Assist','Union Labour Standard','Labour Mileage','Helper','Labour Overtime','Technician Travel','Crew Leader','Handling'],
    'Pickup Service': ['Pickup Rate','Pickup Date Time Specific','Pickup Straight Truck','Pickup Tailgate','Pickup Inside','Pickup After Hours','Pickup Attempt','Pickup Origin','Pickup Time Specific','Pickup Weekend','Pickup Residential','Pickup Missed','Pickup Extra','Pickup Certified','Pickup Single Shipment','Pickup Detention'],
    'Service Program': ['Freight Solvers US','Freight Broker Sales US','FBI Warehouse Sales','Freight Solvers Audit','FBI Warehouse Sales US','Freight Solvers','Trailer Build','Freight Broker Sales'],
    'Surcharge Service': ['Fuel Surcharge','Florida Inbound','Carbon Surcharge','California Surcharge'],
    'Tradeshow Service': ['Tradeshow Pickup','Tradeshow Delivery','Tradeshow Administration'],
    'Trailer Build Service': ['Build Delivery','Build Storage','Build Shunt','Build Waiting Time','Build Missed Pickup','Build License Plates','Build Nets','Build Detention','Build Wrap','Build Purchase','Build Construction','Build Truck Wash','Build Maintenance','Build Cleaning','Build Cover Storage','Build Material','Build Consulting','Build Permit','Build GPS','Build Repair','Build Shelf Covers']
  };

  /* Dynamic address option lists */
  customerAddresses: IAddressOption[] = [];
  shipperAddresses: IAddressOption[] = [];
  consigneeAddresses: IAddressOption[] = [];

  customerHasProfile: boolean = false;
  changedCustomerProfile: boolean = false;

  /* Validator bookkeeping */
  private allScopeReqKeys: string[] = Array.from(
    new Set(
      Object.values(SCOPE_REQUIRED_FIELDS).reduce((a, arr) => a.concat(arr), [] as string[])
    )
  );

  constructor(
    private fb: FormBuilder,
    private contactSvc: ContactAutocompleteService,
    private menuSvc: MenuService,
    private http: HttpClient,
    private dispatchFreightApi: DispatchFreightApiService,
    private toastr: ToastrService,
    private dialog: MatDialog
  ) {}

  /* ────────────────────────────────────────────────────────────
     Getters for Template
  ──────────────────────────────────────────────────────────── */
  get customerNameControl(): FormControl {
    return this.customerForm.get('customer_name') as FormControl;
  }
  get shipperNameControl(): FormControl {
    return this.shipperForm.get('shipper_name') as FormControl;
  }
  get consigneeNameControl(): FormControl {
    return this.consigneeForm.get('consignee_name') as FormControl;
  }
  get selectedScopeIds(): number[] {
    const ids = this.loadDetailsForm.get('lading_scope')?.value;
    return Array.isArray(ids) ? ids : [];
  }

  /* ────────────────────────────────────────────────────────────
     Lifecycle
  ──────────────────────────────────────────────────────────── */
  ngOnInit(): void {
    this.buildForms();
    this.menuSvc.getUserEmail().subscribe(email => (this.userEmail = email));

    // Build lookup for chip display
    this.ladingScopes.forEach(s => (this.ladingScopesById[s.id] = s));

    (this.loadDetailsForm.get('lading_scope') as FormControl)
      .valueChanges
      .subscribe((ids: number[] = []) => this.onScopeChange(ids));

    [
      this.customerForm.get('customer_uuid'),
      this.customerForm.get('rate_method'),
      this.shipperForm.get('shipper_uuid'),
      this.shipperForm.get('shipper_address'),
      this.shipperForm.get('shipper_postal_code'),
      this.consigneeForm.get('consignee_uuid'),
      this.consigneeForm.get('consignee_address'),
      this.consigneeForm.get('consignee_postal_code'),
    ].forEach((control) => {
      if (!control) return;
      this.dispatchContextSubscriptions.add(control.valueChanges.subscribe(() => {
        this.dispatchFreightRatingRequest = null;
        this.resetDispatchRate();
      }));
    });
  }

  ngOnDestroy(): void {
    this.dispatchRateSubscription?.unsubscribe();
    this.dispatchContextSubscriptions.unsubscribe();
  }

  /* ────────────────────────────────────────────────────────────
     Wizard nav helpers
  ──────────────────────────────────────────────────────────── */
  go(i: number)   { 
    this.active = i; 
    if (this.active == 4 && !this.dispatchFreightPhase1Enabled){
      if (this.doesCustomerTriggerAutomaticRating()) 
        this.loadRater(); 
    }
  }
  next(){ 
    if (this.active < this.steps.length - 1) 
      this.active++; 
    if (this.active == 4 && !this.dispatchFreightPhase1Enabled){
      if (this.doesCustomerTriggerAutomaticRating()) 
        this.loadRater(); 
    }
  }
  prev()          { if (this.active > 0) this.active--; } 

  doesCustomerTriggerAutomaticRating(): boolean{
    if (!this.customerForm.get('customer_uuid')?.value) return false;
    const customer_contact_uuid = this.customerForm.get('customer_uuid')?.value;
    const url = "/api/freightmancer/customer_automatic_rating_check.php?contact_uuid=" + customer_contact_uuid;
    this.http.get<{success: boolean, code: number, message: boolean | string}>(url).subscribe(
      res => {
        if (res.code === 200 && typeof res.message === 'boolean') return res.message;
        return false;
      },
      error => { console.log(error); return false; }
    )
    return false;
  }

  /* ────────────────────────────────────────────────────────────
     Form construction
  ──────────────────────────────────────────────────────────── */
  private buildForms(): void {
    /* Customer */
    this.customerForm = this.fb.group({
      customer_uuid: ['', Validators.required],
      customer_name: [''],
      customer_address: ['', Validators.required],
      customer_postal_code: [''],
      customer_shipping_bol: [false],
      customer_invoice_bol: [false],
      customer_invoice_pod_required: [false],
      customer_invoice_po_required: [false],
      customer_invoice_po: [''],
      customer_rate_quote: [false],
      customer_consignee_agreement: [false],
      customer_at_pickup_notification: [false],
      customer_delayed_pickup_notification: [false],
      customer_at_delivery_notification: [false],
      customer_delayed_delivery_notification: [false],
      customer_assessorials_incurred_notification: [false],
      customer_csa: [false],
      customer_fast: [false],
      customer_ace: [false],
      customer_aci: [false],
      customer_ct_pat: [false],
      customer_canada_bond: [false],
      customer_us_bond: [false],
      customer_hazmat: [false],
      customer_protect_from_freezing: [false],
      customer_heated: [false],
      customer_refrigeration: [false],
      customer_food_grade: [false],
      customer_service_charges: this.fb.array([]),
      rate_method: ['1'],
    });

    /* Shipper */
    this.shipperForm = this.fb.group({
      shipper_uuid: ['', Validators.required],
      shipper_name: [''],
      shipper_address: ['', Validators.required],
      shipper_postal_code: [''],
      shipper_loading_dock: [false],
      shipper_pickup_tailgate: [false],
      shipper_truck_space: [false],
      shipper_pickup_straight_truck: [false],
      shipper_bol_required: [false],
      shipper_pickup_reference: [''],
      estimated_date_shipper: [''],
      estimated_time_start_shipper: [''],
      shipper_is_residential: [false],
      shipper_is_commercial: [false],
      shipper_is_job_site: [false],
      shipper_lift_lull: [false],
      shipper_call_site: [false],
      shipper_unmanned_job_site: [false],
      shipper_job_site_contact_name: [''],
      shipper_job_site_contact_phone: [''],
      shipper_is_convention_center: [false],
      shipper_is_pop_up: [false],
      appointment_shipper: [false],
      appointment_date_shipper: [''],
      appointment_time_start_shipper: [''],
      appointment_time_end_shipper: [''],
    });

    /* Consignee */
    this.consigneeForm = this.fb.group({
      consignee_uuid: ['', Validators.required],
      consignee_name: [''],
      consignee_address: ['', Validators.required],
      consignee_postal_code: [''],
      consignee_loading_dock: [false],
      consignee_pickup_tailgate: [false],
      consignee_truck_space: [false],
      consignee_pickup_straight_truck: [false],
      consignee_bol_required: [false],
      consignee_delivery_reference: [''],
      estimated_date_consignee: [''],
      estimated_time_end_consignee: [''],
      consignee_is_residential: [false],
      consignee_is_commercial: [false],
      consignee_is_job_site: [false],
      consignee_lift_lull: [false],
      consignee_call_site: [false],
      consignee_unmanned_job_site: [false],
      consignee_job_site_contact_name: [''],
      consignee_job_site_contact_phone: [''],
      consignee_is_convention_center: [false],
      consignee_is_pop_up: [false],
      appointment_consignee: [false],
      appointment_date_consignee: [''],
      appointment_time_start_consignee: [''],
      appointment_time_end_consignee: [''],
    });

    /* Manifest / Load details */
    this.loadDetailsForm = this.fb.group({
      // Multiple scopes via multi-select; stores an array of IDs
      lading_scope: this.fb.control([], Validators.required),

      // Cross-border broker
      custom_broker_name: [''],

      // Packaging
      packaging_type: this.fb.group({
        palleted: [false],
        carton: [false],
        crate: [false],
        loose_floor: [false],
      }),

      // Operations
      operations: this.fb.group({
        carrier_rated: [false],
        carrier_rerated: [false],
        straps_bars: [false],
        protective_equipment: [false],
        operations_notation: [''],
      }),

      // Equipment
      equipment: this.fb.group({
        type_of_equipment: [''],
        equipment_options: [''],
        partial_or_full: ['Partial'],
      }),

      // Household Import/Export
      household_import: this.fb.group({
        importing_passport: [false],
        importing_work_permit_visa: [false],
        importing_inventory_listing: [false],
        importing_new_canadian_address: [false],
        importing_scale_tickets: [false],
      }),
      household_export: this.fb.group({
        exporting_passport: [false],
        exporting_form_3299: [false],
        exporting_power_of_attorney: [false],
        exporting_supplemental_declaration: [false],
        exporting_inventory_listing: [false],
        exporting_i94: [false],
        exporting_scale_tickets: [false],
      }),

      // Dynamic collections
      pallets_products: this.fb.array([]),
      service_charges: this.fb.array([]),

      // Additional info
      additional_info: this.fb.group({
        product_status: ['1'],
        booking_number: [''],
        release_number: [''],
        lading_number: [''],
        trip_number: [''],
        shipment_number: [''],
        requirements: [''],
        load_details: [''],
        load_special_info: [''],
        load_length: [''],
        load_value_unit: ['USD'],
        load_value: [''],
        lading_status: [''],
      }),
    });

    /* Rates */
    this.ratesForm = this.fb.group({
      carrier_company_name: [''],
      shipper_radius: ['5'],
      consignee_radius: ['5'],
    });

    /* Lading Status */
    this.statusForm = new FormControl({ value: 'Unassigned', disabled: false})
  }

  /* ────────────────────────────────────────────────────────────
     Dynamic form array helpers
  ──────────────────────────────────────────────────────────── */
  get palletsProducts(): FormArray {
    return this.loadDetailsForm.get('pallets_products') as FormArray;
  }
  get serviceCharges(): FormArray {
    return this.loadDetailsForm.get('service_charges') as FormArray;
  }
  get customerServiceCharges(): FormArray {
    return this.customerForm.get('customer_service_charges') as FormArray;
  }

  addPalletProductSet(): void {
    const fg = this.fb.group({
      mode: ['pallet'],
      pallet_num: [''],
      pallet_length: [''],
      pallet_width: [''],
      pallet_height: [''],
      pallet_weight: [''],
      product_model_number: [''],
      product_quantity: [''],
      product_length: [''],
      product_width: [''],
      product_height: [''],
      product_weight: [''],
      product_description: [''],
    });
    this.palletsProducts.push(fg);
  }

  removePalletProductSet(i: number): void {
    this.palletsProducts.removeAt(i);
  }

  private newServiceCharge(service: IDefaultService, origin: 'shipper' | 'consignee'): FormGroup {
    return this.fb.group({
      origin: [origin],
      is_default: [true],
      service_uuid: [service.contact_profile_default_service_uuid, Validators.required],
      service: [service.service],
      service_type: [service.service_type],
    });
  }

  addManualServiceCharge(): void {
    this.serviceCharges.push(this.fb.group({
      origin: ['shipper', Validators.required],
      is_default: [false],
      service_uuid: [null],
      service: ['', Validators.required],
      service_type: ['', Validators.required],
    }));
  }

  removeServiceCharge(i: number): void {
    this.serviceCharges.removeAt(i);
  }

  private newCustomerServiceCharge(service: IDefaultService): FormGroup {
    return this.fb.group({
      is_default: [true],
      service_uuid: [service.contact_profile_default_service_uuid, Validators.required],
      service: [service.service],
      service_type: [service.service_type],
    });
  }

  addManualCustomerServiceCharge(): void {
    this.customerServiceCharges.push(this.fb.group({
      is_default: [false],
      service_uuid: [null],
      service: ['', Validators.required],
      service_type: ['', Validators.required],
    }));
  }

  removeCustomerServiceCharge(i: number): void {
    this.customerServiceCharges.removeAt(i);
  }

  onServiceCategoryChange(serviceControl: AbstractControl): void {
    const serviceGroup = serviceControl as FormGroup;
    serviceGroup.get('service_type')?.reset('');
  }

  /* ────────────────────────────────────────────────────────────
     Scope handling (multi-select)
  ──────────────────────────────────────────────────────────── */
  private getCtrl(path: string): AbstractControl | null {
    return path.split('.').reduce<AbstractControl | null>(
      (ctrl, key) => (ctrl ? ctrl.get(key) : null),
      this.loadDetailsForm as AbstractControl
    );
  }

  hasScope(id: number): boolean {
    const ids = this.loadDetailsForm.get('lading_scope')?.value as number[] | null;
    return Array.isArray(ids) && ids.includes(id);
  }

  private onScopeChange(ids: number[] = []): void {
    const unionReq: string[] = Array.from(new Set(
      ids.reduce((a, id) => a.concat(SCOPE_REQUIRED_FIELDS[id] || []), [] as string[])
    ));

    // clear previous validators for all known scope-required fields
    this.allScopeReqKeys.forEach(p => {
      const c = this.getCtrl(p);
      if (c) { c.clearValidators(); c.updateValueAndValidity({ emitEvent: false }); }
    });

    // apply validators for the current selection
    unionReq.forEach(p => {
      const c = this.getCtrl(p);
      if (c) { c.setValidators([Validators.required]); c.updateValueAndValidity({ emitEvent: false }); }
    });

    // clear broker if no crossborder scope
    if (!this.hasScope(3) && !this.hasScope(4)) {
      this.loadDetailsForm.get('custom_broker_name')!.setValue('');
    }

    // convenience defaults based on common scopes
    if (this.hasScope(18) && !this.hasScope(19)) {
      this.shipperForm.patchValue({ shipper_is_residential: true, shipper_is_commercial: false });
      this.consigneeForm.patchValue({ consignee_is_residential: true, consignee_is_commercial: false });
    } else if (this.hasScope(19) && !this.hasScope(18)) {
      this.shipperForm.patchValue({ shipper_is_residential: false, shipper_is_commercial: true });
      this.consigneeForm.patchValue({ consignee_is_residential: false, consignee_is_commercial: true });
    }
    if (this.hasScope(26)) {
      this.shipperForm.patchValue({ shipper_is_job_site: true });
      this.consigneeForm.patchValue({ consignee_is_job_site: true });
    }
  }

  removeScope(id: number): void {
    const ctrl = this.loadDetailsForm.get('lading_scope') as FormControl<number[]>;
    const currentScopes = ctrl.value || [];
    const newScopes = currentScopes.filter(scopeId => scopeId !== id);
    ctrl.setValue(newScopes); // triggers valueChanges -> onScopeChange
  }

  trackById(index: number, id: number): number {
    return id;
  }

  /* ────────────────────────────────────────────────────────────
     Contact auto-complete handlers
  ──────────────────────────────────────────────────────────── */
  onCustomerSelected(opt: ContactAutocompleteResult): void {
    this.customerForm.patchValue({
      customer_uuid: opt.value,
      customer_name: opt.label,
    });

    this.contactSvc.getDetails(opt.value).subscribe((data: ContactDetails) => {
      this.customerAddresses = (data.addresses || []).map(addr => ({
        value: addr.contact_address_uuid,
        label: [addr.address, addr.address_extended, addr.locality, addr.region].filter(Boolean).join(', '),
      }));
      if (this.customerAddresses.length) {
        this.customerForm.get('customer_address')!.setValue(this.customerAddresses[0].value);
      }
      this.customerForm.get('customer_postal_code')!.setValue(data.addresses?.[0]?.postal_code || '');

      const profile = data.profile?.[0];
      if (profile) {
        this.customerHasProfile = true;
        const profileMap: { [key: string]: string } = {
          bol_shipping: 'customer_shipping_bol', bol_invoice: 'customer_invoice_bol',
          pod_invoice: 'customer_invoice_pod_required', purchase_order_number_invoice: 'customer_invoice_po_required',
          rate_quote_copy: 'customer_rate_quote', consignee_agreement: 'customer_consignee_agreement',
          at_pick_up: 'customer_at_pickup_notification', delayed_pick_up: 'customer_delayed_pickup_notification',
          at_delivery: 'customer_at_delivery_notification', delayed_delivery: 'customer_delayed_delivery_notification',
          assessorials_incurred: 'customer_assessorials_incurred_notification', use_csa: 'customer_csa',
          use_fast: 'customer_fast', use_ace: 'customer_ace', use_aci: 'customer_aci', use_ct_pat: 'customer_ct_pat',
          use_can_bond: 'customer_canada_bond', use_us_bond: 'customer_us_bond', use_hazmat: 'customer_hazmat',
          protect_freezing: 'customer_protect_from_freezing', heated: 'customer_heated',
          refrigeration: 'customer_refrigeration', use_food_grade: 'customer_food_grade',
          rate_method: 'rate_method',
        };
        const valuesToPatch: { [key: string]: any } = {};
        for (const apiKey in profileMap) {
          if (Object.prototype.hasOwnProperty.call(profile, apiKey)) {
            const formKey = profileMap[apiKey];
            const apiValue = (profile as any)[apiKey];
            if (formKey === 'rate_method') {
              if (apiValue) { valuesToPatch[formKey] = apiValue; }
            } else {
              valuesToPatch[formKey] = !!apiValue;
            }
          }
        }
        this.customerForm.patchValue(valuesToPatch);

        // Mark form and form controls as untouched, so we can see if user modifies any profile-related options when submitting
        this.customerForm.markAsPristine();
        this.customerForm.markAsUntouched();
        Object.values(this.customerForm.controls).forEach(ctrl => ctrl.markAsPristine());
      }

      this.customerServiceCharges.clear();
      if (data.default_services?.length) {
        data.default_services.forEach(service => {
          this.customerServiceCharges.push(this.newCustomerServiceCharge(service));
        });
      }
    });
  }

  onShipperSelected(opt: ContactAutocompleteResult): void {
    this.shipperForm.patchValue({
      shipper_uuid: opt.value,
      shipper_name: opt.label,
    });

    this.contactSvc.getDetails(opt.value).subscribe((data: ContactDetails) => {
      this.shipperAddresses = (data.addresses || []).map(addr => ({
        value: addr.contact_address_uuid,
        label: [addr.address, addr.address_extended, addr.locality, addr.region].filter(Boolean).join(', '),
      }));
      if (this.shipperAddresses.length) {
        this.shipperForm.get('shipper_address')!.setValue(this.shipperAddresses[0].value);
      }
      this.shipperForm.get('shipper_postal_code')!.setValue(data.addresses?.[0]?.postal_code || '');

      // Remove existing shipper-origin services
      for (let i = this.serviceCharges.controls.length - 1; i >= 0; i--) {
        if (this.serviceCharges.at(i).value.origin === 'shipper') {
          this.serviceCharges.removeAt(i);
        }
      }
      // Add new defaults
      if (data.default_services?.length) {
        data.default_services.forEach(service => {
          this.serviceCharges.push(this.newServiceCharge(service, 'shipper'));
        });
      }
    });
  }

  onConsigneeSelected(opt: ContactAutocompleteResult): void {
    this.consigneeForm.patchValue({
      consignee_uuid: opt.value,
      consignee_name: opt.label,
    });

    this.contactSvc.getDetails(opt.value).subscribe((data: ContactDetails) => {
      this.consigneeAddresses = (data.addresses || []).map(addr => ({
        value: addr.contact_address_uuid,
        label: [addr.address, addr.address_extended, addr.locality, addr.region].filter(Boolean).join(', '),
      }));
      if (this.consigneeAddresses.length) {
        this.consigneeForm.get('consignee_address')!.setValue(this.consigneeAddresses[0].value);
      }
      this.consigneeForm.get('consignee_postal_code')!.setValue(data.addresses?.[0]?.postal_code || '');

      // Remove existing consignee-origin services
      for (let i = this.serviceCharges.controls.length - 1; i >= 0; i--) {
        if (this.serviceCharges.at(i).value.origin === 'consignee') {
          this.serviceCharges.removeAt(i);
        }
      }
      // Add new defaults
      if (data.default_services?.length) {
        data.default_services.forEach(service => {
          this.serviceCharges.push(this.newServiceCharge(service, 'consignee'));
        });
      }
    });
  }

  /* ────────────────────────────────────────────────────────────
     Rater
  ──────────────────────────────────────────────────────────── */
  get dispatchFreightBridge(): FreightmancerDispatchBridge {
    return adaptFreightmancerDispatch({
      customerUuid: this.customerForm.get('customer_uuid')?.value,
      shipperUuid: this.shipperForm.get('shipper_uuid')?.value,
      shipperAddressUuid: this.shipperForm.get('shipper_address')?.value,
      shipperPostalCode: this.shipperForm.get('shipper_postal_code')?.value,
      consigneeUuid: this.consigneeForm.get('consignee_uuid')?.value,
      consigneeAddressUuid: this.consigneeForm.get('consignee_address')?.value,
      consigneePostalCode: this.consigneeForm.get('consignee_postal_code')?.value,
      rateMethod: this.customerForm.get('rate_method')?.value,
    });
  }

  get dispatchAppendNewLegDetails(): DispatchAppendNewLegDetails {
    const additionalInfo = this.loadDetailsForm.get('additional_info')?.value as Record<string, unknown> | null;
    return {
      load_start: this.dispatchLocalDateTime(
        this.shipperForm.get('estimated_date_shipper')?.value,
        this.shipperForm.get('estimated_time_start_shipper')?.value,
      ),
      load_end: this.dispatchLocalDateTime(
        this.consigneeForm.get('estimated_date_consignee')?.value,
        this.consigneeForm.get('estimated_time_end_consignee')?.value,
      ),
      load_details: this.dispatchOptionalText(additionalInfo?.['load_details']),
      load_special_info: this.dispatchOptionalText(additionalInfo?.['load_special_info']),
      requirements: this.dispatchOptionalText(additionalInfo?.['requirements']),
      customer_po: this.dispatchOptionalText(this.customerForm.get('customer_invoice_po')?.value),
      pickup_reference_number: this.dispatchOptionalText(this.shipperForm.get('shipper_pickup_reference')?.value),
      delivery_reference_number: this.dispatchOptionalText(this.consigneeForm.get('consignee_delivery_reference')?.value),
      booking_number: this.dispatchOptionalText(additionalInfo?.['booking_number']),
      release_number: this.dispatchOptionalText(additionalInfo?.['release_number']),
    };
  }

  onDispatchFreightChange(draft: DispatchFreightDraft): void {
    this.dispatchFreightDraft = draft;
    this.dispatchFreightRatingRequest = null;
    this.resetDispatchRate();
  }

  onDispatchRateRequested(request: DispatchRatingRequest): void {
    this.dispatchFreightRatingRequest = request;
    const requestSequence = ++this.dispatchRateRequestSequence;
    this.dispatchRateSubscription?.unsubscribe();
    this.dispatchRateState = 'loading';
    this.dispatchRateResponse = null;
    this.dispatchRateError = '';

    this.dispatchRateSubscription = this.dispatchFreightApi.rate(request).subscribe({
      next: (response) => {
        if (requestSequence !== this.dispatchRateRequestSequence) return;
        this.dispatchRateResponse = response;
        this.dispatchRateState = response.ok && response.status === 'rated'
          ? 'rated'
          : 'needs_input';
      },
      error: (error: HttpErrorResponse) => {
        if (requestSequence !== this.dispatchRateRequestSequence) return;
        this.dispatchRateResponse = null;
        this.dispatchRateState = 'error';
        this.dispatchRateError = this.dispatchRateErrorText(error);
      },
    });
  }

  private resetDispatchRate(): void {
    this.dispatchRateRequestSequence += 1;
    this.dispatchRateSubscription?.unsubscribe();
    this.dispatchRateSubscription = null;
    this.dispatchRateState = 'idle';
    this.dispatchRateResponse = null;
    this.dispatchRateError = '';
  }

  private dispatchRateErrorText(error: HttpErrorResponse): string {
    if (typeof error.error === 'string' && error.error.trim()) return error.error.trim();
    if (error.error && typeof error.error === 'object') {
      const payload = error.error as { message?: unknown; error?: unknown };
      if (typeof payload.message === 'string' && payload.message.trim()) return payload.message.trim();
      if (typeof payload.error === 'string' && payload.error.trim()) return payload.error.trim();
    }
    return error.message || 'Unable to retrieve an authoritative customer rate.';
  }

  private dispatchLocalDateTime(dateValue: unknown, timeValue: unknown): string | undefined {
    let date = '';
    if (dateValue instanceof Date && Number.isFinite(dateValue.getTime())) {
      date = [
        String(dateValue.getFullYear()).padStart(4, '0'),
        String(dateValue.getMonth() + 1).padStart(2, '0'),
        String(dateValue.getDate()).padStart(2, '0'),
      ].join('-');
    } else if (typeof dateValue === 'string') {
      date = dateValue.trim();
    }
    if (!date) return undefined;

    const time = typeof timeValue === 'string' ? timeValue.trim() : '';
    return time ? `${date} ${time}` : date;
  }

  private dispatchOptionalText(value: unknown): string | undefined {
    return typeof value === 'string' ? value : undefined;
  }

  loadRater(): void {
    this.showRater = true;

    const rateType: IRaterArgument['rate_type'] = (() => {
      switch (this.customerForm.get('rate_method')?.value) {
        case '1': return 'WPCF';
        case '2': return 'WEIGHT';
        case '3': return 'LINEAR_FT';
        default:  return 'WEIGHT';
      }
    })();

    this.raterArg = {
      customer_contact_uuid: this.customerForm.get('customer_uuid')?.value,
      rate_type: rateType,
      locations: {
        source: {
          type: 'BY_POSTAL_CODE',
          value: this.shipperForm.get('shipper_postal_code')?.value
        },
        destination: {
          type: 'BY_POSTAL_CODE',
          value: this.consigneeForm.get('consignee_postal_code')?.value
        }
      },
      shipment: {
        pallets: this.reformatPalletData()
      }
    } as IRaterArgument;

    console.log('[Rater Arg]', this.raterArg);
  }

  reformatPalletData(): IRaterPallet[] {
    const original = this.palletsProducts.getRawValue();
    const fixed: IRaterPallet[] = [];
    for (const p of original) {
      fixed.push({
        weight: Number(p.pallet_weight || 0),
        width: Number(p.pallet_width || 0),
        height: Number(p.pallet_height || 0),
        length: Number(p.pallet_length || 0),
        number: Number(p.pallet_num || 0),
      });
    }
    return fixed;
  }

  setCarrier(value: string): void {
    this.carrierContactUuid = value;
    if(this.carrierContactUuid.length > 0)
      this.statusForm.disable();
    this.toastr.success('Carrier has been set.');
    console.log('[Carrier Set]', this.carrierContactUuid);
  }

  /* ────────────────────────────────────────────────────────────
     Final submission
  ──────────────────────────────────────────────────────────── */
  hasUserModifiedProfile(): boolean {
    if (!this.customerHasProfile)
      return false;

    const ignored = new Set(['customer_uuid', 'customer_name', 'customer_address', 'customer_postal_code', 'rate_method']);
    
    return Object.entries(this.customerForm.controls)
    .filter(([name]) => !ignored.has(name)) // skip looking at controls with names in ignored list
    .some(([, control]) => control.dirty); // have any controls been changed?
  }

  saveCustomerProfile(): void{
    
  }

  submit(): void {
    // TESTING
    // when done testing, add code to show a modal to ask user if they want to change the profile to current settings
    if (this.hasUserModifiedProfile()){
      this.dialog.open(this.customerProfileSaveDialog, {
        width: '400px'
      });
    }
    return;

    let palletProducts: IPalletProductPayload[];
    try {
      palletProducts = this.dispatchFreightPhase1Enabled
        ? dispatchFreightToLegacyPalletProducts(this.dispatchFreightDraft)
        : this.palletsProducts.getRawValue() as IPalletProductPayload[];
    } catch (error: unknown) {
      this.toastr.error(this.dispatchFreightMappingErrorText(error));
      return;
    }

    const merged = {
      ...this.customerForm.value,
      ...this.shipperForm.value,
      ...this.consigneeForm.value,
      ...this.loadDetailsForm.value,
      ...this.ratesForm.value,
      pallets_products: palletProducts,
      carrier_contact_uuid: this.carrierContactUuid || undefined,
      lading_status: this.statusForm.value || undefined,
      action: 'cast_work_order' as const
    };

    const payload: IFreightmancerPayload = merged;

    console.log('🚚 Freight spell cast!', JSON.stringify(payload));
    this.http.post<any>('/api/freightmancer/freightmancer.php', payload).subscribe(
      (response) => {
        console.log(response);
        if (response.code && response.code === 200) {
          this.toastr.success(
            `Lading created! Lading uuid: ${response.lading_uuid} and lading id: ${response.lading_id}`
          );
          window.open(`https://newams2.ulaap.com/work_order_details/${response.lading_uuid}`);
        }
      },
      (error) => {
        console.error(error);
        this.toastr.error('Something went wrong creating the lading.');
      }
    );
  }

  private dispatchFreightMappingErrorText(error: unknown): string {
    if (error instanceof DispatchFreightLegacyPayloadError) {
      return (error as DispatchFreightLegacyPayloadError).message;
    }

    return 'Unable to map freight into the lading payload.';
  }
}
